import { useState } from "react";
import { motion } from "framer-motion";
import { Clock, Check, Eye, MessageCircle, Users, X, CalendarDays, Info } from "lucide-react";
import { PACKAGES, waLink } from "@/data/siteData";

const cardAnim = {
  hidden: { opacity: 0, y: 36 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.14, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

function PackageModal({ pkg, onClose }) {
  if (!pkg) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="package-details-modal"
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-navy-abyss/80 backdrop-blur-md"
        onClick={onClose}
        data-testid="package-modal-backdrop"
      />
      <motion.div
        initial={{ opacity: 0, y: 60, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-navy-card border border-gold/25 shadow-2xl"
      >
        <div className="relative h-52 sm:h-64">
          <img src={pkg.image} alt={pkg.alt} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-card via-navy-card/20 to-transparent" />
          <button
            onClick={onClose}
            aria-label="Close details"
            data-testid="package-modal-close-btn"
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-navy-abyss/70 backdrop-blur border border-white/15 flex items-center justify-center text-ivory hover:bg-navy-abyss transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <span className="absolute top-4 left-4 bg-gold text-navy-abyss text-[10px] font-bold tracking-[0.18em] px-3 py-1.5 rounded-full">
            {pkg.badge}
          </span>
        </div>

        <div className="p-6 sm:p-8 -mt-8 relative">
          <p className="text-[11px] uppercase tracking-[0.28em] text-gold font-semibold">{pkg.duration}</p>
          <h3 className="font-display text-2xl sm:text-3xl font-bold mt-2" data-testid="package-modal-title">{pkg.name}</h3>
          <div className="flex items-baseline gap-2 mt-3 flex-wrap">
            <span className="font-display text-3xl sm:text-4xl gold-text font-bold" data-testid="package-modal-price">{pkg.price}/-</span>
            <span className="text-xs text-slate-400">per person</span>
            {pkg.designedFor && (
              <span className="text-xs text-slate-400 flex items-center gap-1"><Users className="w-3.5 h-3.5" />{pkg.designedFor}</span>
            )}
          </div>

          <div className="mt-6">
            <p className="text-xs uppercase tracking-[0.22em] text-slate-400 mb-3">Package Inclusions</p>
            <ul className="space-y-2.5">
              {pkg.inclusions.map((inc) => (
                <li key={inc} className="flex items-start gap-3 text-sm text-slate-200">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-gold/15 flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-gold" />
                  </span>
                  {inc}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-7">
            <p className="text-xs uppercase tracking-[0.22em] text-slate-400 mb-3 flex items-center gap-2">
              <CalendarDays className="w-3.5 h-3.5 text-gold" /> Day-by-Day Itinerary
            </p>
            <div className="space-y-4">
              {pkg.itinerary.map((d) => (
                <div key={d.day} className="flex gap-4">
                  <span className="font-cinzel text-[10px] tracking-[0.14em] text-gold border border-gold/30 rounded-full px-3 py-1 h-fit whitespace-nowrap mt-0.5">
                    {d.day}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-ivory">{d.title}</p>
                    <p className="text-xs text-slate-400 leading-relaxed mt-1">{d.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-6 text-[11px] text-slate-500 leading-relaxed flex gap-2">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gold/60" />
            Final pricing is confirmed by our travel team based on your travel dates, group size and hotel preference.
          </p>

          <a
            href={waLink(`Hi RAJ TRAVELS GOA! I'm interested in the "${pkg.name}" (${pkg.duration}, ${pkg.price}/-). Please share full details and availability.`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="package-modal-enquire-btn"
            className="mt-6 flex items-center justify-center gap-2 w-full bg-gold text-navy-abyss font-semibold py-4 rounded-full hover:bg-gold-hover transition-colors duration-300"
          >
            <MessageCircle className="w-5 h-5" />
            Enquire About This Package on WhatsApp
          </a>
        </div>
      </motion.div>
    </div>
  );
}

export default function Packages() {
  const [active, setActive] = useState(null);

  return (
    <section id="packages" data-testid="packages-section" className="relative py-20 sm:py-28 px-5 sm:px-8 max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-12 sm:mb-16"
      >
        <p className="text-[11px] sm:text-xs uppercase tracking-[0.32em] text-gold font-semibold mb-4">Handcrafted For You</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-2xl">
          Featured <span className="gold-text italic">Goa Packages</span>
        </h2>
        <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-xl leading-relaxed">
          Our most loved holidays — transparent inclusions, comfortable stays and seamless transfers, arranged by Goan locals.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {PACKAGES.map((pkg, i) => (
          <motion.article
            key={pkg.id}
            custom={i}
            variants={cardAnim}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            data-testid={`package-card-${pkg.id}`}
            className="card-sheen group rounded-3xl bg-navy-card border border-[#DC2626]/20 hover:border-gold/60 shadow-sm transition-[border-color,transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-xl overflow-hidden flex flex-col"
          >
            <div className="relative h-56 sm:h-60 overflow-hidden">
              <img
                src={pkg.image}
                alt={pkg.alt}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-card via-transparent to-navy-abyss/30" />
              <span className="absolute top-4 left-4 bg-gold text-navy-abyss text-[10px] font-bold tracking-[0.18em] px-3 py-1.5 rounded-full shadow-lg">
                {pkg.badge}
              </span>
              <span className="absolute bottom-4 left-4 flex items-center gap-1.5 text-xs text-ivory/90 bg-navy-abyss/60 backdrop-blur px-3 py-1.5 rounded-full">
                <Clock className="w-3.5 h-3.5 text-gold" /> {pkg.duration}
              </span>
            </div>

            <div className="p-6 flex flex-col flex-1">
              <h3 className="font-display text-xl sm:text-2xl font-semibold leading-snug">{pkg.name}</h3>
              {pkg.designedFor && (
                <p className="mt-1.5 text-xs text-slate-400 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-gold/70" /> Designed for: {pkg.designedFor}
                </p>
              )}
              <div className="mt-3 flex items-baseline gap-1.5">
                <span className="font-display text-3xl gold-text font-bold" data-testid={`package-price-${pkg.id}`}>{pkg.price}</span>
                <span className="text-slate-400 text-sm">/- per person</span>
              </div>

              <ul className="mt-5 space-y-2 flex-1">
                {pkg.inclusions.map((inc) => (
                  <li key={inc} className="flex items-start gap-2.5 text-[13px] text-slate-300">
                    <Check className="w-4 h-4 text-gold shrink-0 mt-0.5" />
                    {inc}
                  </li>
                ))}
              </ul>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  onClick={() => setActive(pkg)}
                  data-testid={`package-view-details-${pkg.id}`}
                  className="inline-flex items-center justify-center gap-1.5 border border-gold/40 text-gold-light text-sm font-medium py-3 rounded-full hover:bg-gold/10 transition-colors duration-300"
                >
                  <Eye className="w-4 h-4" /> View Details
                </button>
                <a
                  href={waLink(`Hi RAJ TRAVELS GOA! I'd like to enquire about the "${pkg.name}" (${pkg.duration}, ${pkg.price}/-).`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`package-enquire-${pkg.id}`}
                  className="inline-flex items-center justify-center gap-1.5 bg-gold text-navy-abyss text-sm font-semibold py-3 rounded-full hover:bg-gold-hover transition-colors duration-300"
                >
                  <MessageCircle className="w-4 h-4" /> Enquire Now
                </a>
              </div>
            </div>
          </motion.article>
        ))}
      </div>

      <p className="mt-8 text-center text-xs text-slate-500" data-testid="packages-pricing-note">
        Final pricing is confirmed by our travel team based on your dates, group size and preferences — no hidden costs, ever.
      </p>

      {active && <PackageModal pkg={active} onClose={() => setActive(null)} />}
    </section>
  );
}
